import { Form, FormProps, notification } from 'antd';
import useModal from 'stores/useModal';

import { useMutation } from '@tanstack/react-query';
import { postRefund } from 'services/api/module/booking.api';
import { queryClient } from 'index';
import QUERY_KEYS from 'services/api/queryKeys';
import 'styles/post-refund-transaction.scss';
import { useGetBookingDetail } from 'hooks/useGetBookingDetail';
import { useGetMe } from 'hooks/useGetMe';
import { useGetGuestOrderDetail, useGetTransactionCodes } from 'hooks/useGetGuestTransactions';
import { toLocalTime } from 'utils';
import { RenderPostTransactionModal } from 'components/modal/transaction-modal/utils/renderPostTransactionModal';
import { useTranslation } from 'react-i18next';

function PostRefundTransaction() {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const {
    postRefundTransaction: {
      folio_balance_code,
      booking_line_id,
      guest_id,
      orderId,
      isOpenPostRefundTransaction
    },
    closePostRefundTransaction
  } = useModal();

  const { data: bookingLineDetail } = useGetBookingDetail(booking_line_id);
  const { data: userData } = useGetMe();
  const { data: transactionCodes } = useGetTransactionCodes();
  const { data: orderDetails } = useGetGuestOrderDetail(orderId);

  const uniqueTransactionCodesName = new Set([
    transactionCodes
      ?.filter(item => Number(item.sub_code) > 0 && item.transaction_group === 'refund')
      .map(item => item.name)
  ]);

  const { mutateAsync: mutatePostRefundTransaction, isPending } = useMutation({
    mutationFn: (params: any) =>
      postRefund({
        guest_id: params.guest_id,
        folio_balance_code: params.folio_balance_code,
        note: params.note,
        transaction_code_id: params.transaction_code_id,
        amount: params.amount,
        transaction_date: params.transaction_date
      })
  });

  const handleCloseModal = () => {
    closePostRefundTransaction();
  };

  const onFinish: FormProps['onFinish'] = async (values: any) => {
    try {
      await mutatePostRefundTransaction({
        guest_id: guest_id,
        folio_balance_code: folio_balance_code,
        note: values.note,
        transaction_code_id: values.transaction_code_id,
        amount: values.amount,
        transaction_date: toLocalTime(values.transaction_date, 'YYYY-MM-DD')
      });
      closePostRefundTransaction();
      form.resetFields();
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_GUEST_TRANSACTIONS, guest_id]
      });
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_GUEST]
      });
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_GUEST_TRANSACTIONS, QUERY_KEYS.GET_GUEST_TRANSACTIONS_DETAIL]
      });
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.KEY_ROOM, QUERY_KEYS.GET_BOOKING_DETAIL, booking_line_id]
      });
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_TRANSACTION_HISTORY_LOGS]
      });
      notification.success({
        message: t('common.message.refundTransaction')
      });
    } catch (err: any) {
      notification.error({
        message: err.error || t('common.error')
      });
    }
  };

  return (
    <RenderPostTransactionModal
      title={t('cashierPage.refund.title')}
      isOpen={isOpenPostRefundTransaction}
      handleClose={handleCloseModal}
      onFinish={onFinish}
      form={form}
      isPending={isPending}
      folio_balance_code={folio_balance_code}
      guest_id={guest_id}
      bookingLineDetail={bookingLineDetail}
      userData={userData}
      transactionCodes={transactionCodes}
      orderDetails={orderDetails}
      uniqueTransactionCodesName={uniqueTransactionCodesName}
    />
  );
}

export default PostRefundTransaction;
